import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { SlDropdown, SlShowEvent } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { contexts } from '../contexts.js';
import { consumeDOMEvent } from '../utils.js';
import { CalendarRange } from './calendar-range.js';
import { linkStyles } from './cssstyles.js';

@customElement('head-calendar')
class HEADCalendar extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @property({ attribute: false }) headRef?: WireTypes.Ref;
  @property({ attribute: false }) since?: Temporal.PlainDate;
  @property({ attribute: false }) until?: Temporal.PlainDate;
  @property({ attribute: false }) maxRangeDays?: number;

  @query('calendar-range') private _calendarRange?: CalendarRange;
  @query('sl-dropdown') private _calendarDropdown?: SlDropdown;

  render() {
    const since = this.since;
    const until = this.until;
    const timezone = this._project?.timezone;
    const headCommitDay = this.headRef?.commit && timezone ?
      Temporal.Instant
        .fromEpochMilliseconds(this.headRef.commit.timestamp)
        .toZonedDateTimeISO(timezone)
        .toPlainDate()
      : undefined;

    return html`
      <sl-dropdown placement=bottom-start distance=10 @sl-show=${(event: SlShowEvent) => {
        // We have to guard against inner sl-tooltips that might show and hide.
        if (until && event.target === this._calendarDropdown)
          this._calendarRange?.revealDate(until);
      }} @sl-hide=${(event: SlShowEvent) => {
        // We have to guard against inner sl-tooltips that might show and hide.
        if (event.target === this._calendarDropdown)
          this._calendarRange?.cancelSelection();
      }}>
        <sl-icon-button slot="trigger" name=calendar4-range>          
        </sl-icon-button>
        <v-box class=calendar-picker>
          <calendar-range
            @rangeend=${(event: CustomEvent<Date>) => {
              if (!this._calendarRange || !this._calendarRange.since || !this._calendarRange.until)
                return;
              consumeDOMEvent(event);
              this._setCalendarRange({
                since: this._calendarRange.since,
                until: this._calendarRange.until,
              });
            }}
            .maxRangeDays=${this.maxRangeDays}
            .since=${since}
            .until=${until}
            .max=${timezone ? Temporal.Now.plainDateISO(timezone) : nothing}
          ></calendar-range>
          <h-box>
            <x-filler></x-filler>
            <sl-icon-button name=crosshair @click=${() => {
              if (headCommitDay)
                this._calendarRange?.revealDate(headCommitDay);
            }}></sl-icon-button>
          </h-box>
        </v-box>
      </sl-dropdown>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues): void {
    if (!this.since && !this.until && this.headRef && this._project) {
      this._calendarRange?.revealDate(Temporal.Instant
        .fromEpochMilliseconds(this.headRef.commit.timestamp)
        .toZonedDateTimeISO(this._project.timezone)
        .toPlainDate()
      );
    }
  }

  private _setCalendarRange({ since, until }: { since: Temporal.PlainDate, until: Temporal.PlainDate }) {
    if (!this.headRef)
      return;
    this.since = since;
    this.until = until;
    this._calendarRange?.revealDate(until);

    this.dispatchEvent(new CustomEvent<{ since: Temporal.PlainDate, until: Temporal.PlainDate }>('fk-select', {
      detail: {
        since: this.since,
        until: this.until,
      }
    }));
  }

  static styles = [linkStyles, css`
    /* Sized to a row of days in <test-health-calendar>, where this sits. */
    sl-icon-button[slot=trigger]::part(base) {
      padding: 0;
    }

    .calendar-picker {
      background-color: white;
      padding: var(--sl-spacing-small);
      border: 1px solid var(--fk-color-border);
      box-shadow: var(--fk-shadow-floating-small);
      border-radius: var(--sl-border-radius-large);
    }
  `];
}
